<template>
  <div :key="mainIndId" class="indicator" :layout="layout">
    <template v-if="!error">
      <indicator-header v-if="!isThumbnail" :country-selection="countrySelection"
        :data-countries="filteredDataCountries" :constraints="mainConstraints"
        :breakdown-selection="mainBreakdownSelection" :breakdown-dimensions="mainBreakdownDimensions"
        :flip-dimensions="flipDimensions" @update="updateSelection($event)" />
      <div class="indicator__outputs">
        <div class="indicator__chart-header">
          <unit-selector v-if="mainUnits.length > 1 && mainUnit" v-model="mainUnit" :selection="mainUnit"
            :values="mainUnits" data-align="bottom" />
          <chart-type-selector v-if="isExploreLayout" v-model="chartType" :options="chartTypes"
            :disablements="disabledChartTypes" />
        </div>
        <template v-if="chartType === 'table'">
          <data-table :data="tableData('main')" />
        </template>
        <template v-else>
          <div v-if="loading" class="indicator__loader">
            <div class="text">Loading</div>
          </div>
          <template v-else>
            <template v-if="currentChartData.length > 1">
              <div class="chart-container">
                <chart :key="chartType" v-model="focus" :selection="countrySelection" :dataset="currentChartData"
                  :type="chartType" :time-scale="timeScale.id" :time-interval="chartTimeInterval"
                  :evolutive-data="evolutiveData" :units="{ y: mainUnit }" @update:selection="
                    updateSelection({ slot: 'main', dim: 'countries', value: $event })
                    " @isocodes="storeIsoCodes($event)" />
                <chart-controls v-if="isExploreLayout" :class="'indicator__controls-pannel'" :mode="'indicator'"
                  :chart-data="currentChartData" />
              </div>
            </template>
            <div v-else class="indicator__error">
              <div class="text">No data available for your selection</div>
            </div>
          </template>
          <div v-if="!(isPrintable || isThumbnail)" class="indicator__time-selector">
            <time-scale-selector v-if="mainTimeScales.length > 1" v-model="timeScale" :selection="timeScale"
              :values="mainTimeScales" data-align="top" />
            <slider-selector v-if="timeSelection" v-model="sliderTimeSelection" :data="timeSliderValues"
              :data-value="'value'" :data-label="'label'" :disabled="lastDates || mainAvailableDates.length <= 1"
              :hide-tooltip="isMobile || lastDates" />
            <div class="indicator__time-mode" :class="{ 'last-dates': lastDates }">
              <switcher-selector v-model="lastDates" :disabled="chartType === 'linechart' || chartType === 'heatmap'" />
              <label :class="{ 'fade-out': chartType === 'linechart' || chartType === 'heatmap' }">Latest data
                available</label>
            </div>
          </div>
        </template>
      </div>
      <chart-type-selector v-if="isExploreLayout" v-model="chartType" :class="'responsive'" :options="chartTypes"
        :disableds="disabledChartTypes" />
    </template>
    <template v-else>
      <div class="mocked-app-image" />
    </template>
  </div>
</template>

<script>
import SwitcherSelector from '../selectors/SwitcherSelector.vue'
import SliderSelector from '../selectors/SliderSelector.vue'
import TimeScaleSelector from '../selectors/TimeScaleSelector.vue'
import UnitSelector from '../selectors/UnitSelector.vue'

import ChartDataDriver from '../../mixins/ChartDataDriver.js'
import isFlipped from '../../mixins/isFlipped'

import DataTable from '../DataTable.vue'
import Chart from '../Chart/Chart.vue'

import IndicatorHeader from './IndicatorHeader.vue'
import ChartTypeSelector from './ChartTypeSelector.vue'
import ChartControls from '../ChartControls/ChartControls.vue'

import IndicatorData from './IndicatorData.js'

import { prune as pruneTree } from '@/utils/tree.js'
import { decodeDate } from '@/utils/dates.js'

export default {
  components: {
    DataTable,
    Chart,
    SliderSelector,
    IndicatorHeader,
    ChartTypeSelector,
    ChartControls,
    SwitcherSelector,
    TimeScaleSelector,
    UnitSelector,
  },
  mixins: [ChartDataDriver, isFlipped],
  data() {
    return {
      chartTypes: ['barchart', 'heatmap', 'linechart', 'choropleth'],
      dataCountries: [],
      mapIsoCodes: null,
      focus: null,
    }
  },
  computed: {
    sliderTimeSelection: {
      get() {
        return Array.isArray(this.timeSelection)
          ? this.timeSelection.map(d => d.getTime())
          : this.timeSelection.getTime()
      },
      set(val) {
        if (Array.isArray(val)) {
          this.timeSelection = val.map(d => new Date(d))
        } else {
          this.timeSelection = new Date(val)
        }

        this.$store.commit('utRange', true)
      },
    },
    error() {
      return this.$store.state.xhrError.error
    },
    evolutiveData() {
      if (!this.focus) return [];
      return this.mainChartData.filter(d => d[3] === this.focus)
        .filter(d =>
          Object.keys(this.mainBreakdowns).reduce((a, k) => {
            return a && d.brkDims[k] === this.mainBreakdowns[k]
          }, true)
        )
        .map(_row => {
          let row = [..._row]
          row.brkDims = _row.brkDims
          row[2] = row[2] * this.power('main')
          return row
        })
        .sort((a, b) => a[1] - b[1])
    },
    chartType: {
      get() {
        return this.$store.state.query.chart
      },
      set(chartType) {
        this.$store.commit('chart', chartType)
      },
    },
    disabledChartTypes() {
      const disableds = []
      if (this.isFlipped)
        disableds.push(['choropleth', 'Compare criteria do not match. Try comparing by country.'])
      if (this.mainCubeId && this.mainChartData.length) {
        const timeRange = this.mainAvailableDates.length
        if (
          timeRange === 1 ||
          (/DF_NDSC$/i.test(this.mainCubeId) && this.chartType === 'heatmap')
        ) {
          disableds.push(['linechart', 'This functionality requires data with time evolution'])
          if (!/DF_NDSC$/i.test(this.mainCubeId)) {
            disableds.push(['heatmap', 'This functionality requires data with time evolution'])
          }
        }
      }

      return disableds
    },
    chartTimeInterval() {
      return Array.isArray(this.timeSelection) ? this.timeSelection : [this.timeSelection]
    },
    currentChartData() {
      const dataset = this.mainChartData
      if (!dataset || dataset.length === 0 || !this.chartType) {
        return []
      }

      const isCountrySelected = iso => {
        return this.countryFilter ? (this.selectedCountries[iso] === true && 1) || 0 : true
      }

      let lollipopId, mainOption
      if (this.mainBreakdownDimensions.length) {
        lollipopId = this.mainBreakdownDimensions.find(dim => dim.id === this.lollipopId).id
        mainOption = this.mainBreakdownSelection[this.lollipopId]
      }

      let data
      if (this.chartType === 'barchart') {
        data = new IndicatorData(dataset, {
          fields: ['_COUNTRY_', '_VALUE_', '_LABEL_', '_DATE_'],
          power: this.power('main'),
          timeScale: this.timeScale,
          lollipopInfo: /DF_NDSC$/i.test(this.mainCubeId)
            ? null
            : {
              dimId: lollipopId,
              mainOption: mainOption,
              options: this.lollipopOptions.map(opt => ({
                id: opt.id,
                label: opt.labels[this.lng],
              })),
            },
        })
          .columns(row => {
            return [row[3], row[2], row[0], row[1]]
          })
          .select(row => {
            return row[1].getTime() === this.timeSelection.getTime() && isCountrySelected(row[3])
          })
      } else if (this.chartType === 'choropleth') {
        data = new IndicatorData(this.mainChartData, {
          fields: ['_COUNTRY_', '_VALUE_', '_LABEL_', '_DATE_'],
          power: this.power('main'),
          timeScale: this.timeScale,
        })
          .columns(row => {
            return [row[3], row[2], row[0], row[1]]
          })
          .select(row => {
            return (
              row[1].getTime() === this.timeSelection.getTime() &&
              row.brkDims[lollipopId] === mainOption?.id &&
              isCountrySelected(row[3])
            )
          })
      } else if (this.chartType === 'linechart') {
        data = new IndicatorData(this.mainChartData, {
          fields: ['_LABEL_', '_DATE_', '_VALUE_', '_COUNTRY_'],
          power: this.power('main'),
          timeScale: this.timeScale,
        })
          .columns(row => {
            return row
          })
          .select(row => {
            return (
              row[1].getTime() >= this.timeSelection[0].getTime() &&
              row[1].getTime() <= this.timeSelection[1].getTime() &&
              row.brkDims[lollipopId] === mainOption?.id &&
              isCountrySelected(row[3])
            )
          })
      } else if (this.chartType === 'heatmap') {
        const isNDSC = /DF_NDSC$/i.test(this.mainCubeId) // && !this.$store.state.query.flip
        const brkDim = this.$store.state.cubeMeta.mainBreakdownDimensions[0]
        let dimensionNames
        if (isNDSC === true) {
          dimensionNames = Object.fromEntries(brkDim.options.map(d => [d.id, d.labels.en]))
        }

        const brkDimId = brkDim.id

        data = new IndicatorData(this.mainChartData, {
          fields: ['_COUNTRY_', '_X_', '_Y_', '_COLOR_', '_LABEL_', '_DATE_'],
          power: this.power('main'),
          timeScale: this.timeScale,
        })
          .columns(row => {
            const dateAttr = isNDSC ? decodeDate(row.attributes.YEAR, 'A') : row[1]
            const sorted = [
              row[3] + '--' + row[1].getTime(),
              // row[3] + '--' + dateAttr.getTime(),
              row[3],
              row[1],
              row[2],
              row[0] + '--' + dateAttr.getTime(),
              row[1],
            ]
            if (isNDSC) {
              sorted[2] = dimensionNames[row.brkDims[brkDimId]]
            }
            return sorted
          })
          .select(row => {
            let isSelected = isNDSC
              ? row[1].getTime() === this.timeSelection.getTime()
              : row[1].getTime() >= this.timeSelection[0].getTime() &&
              row[1].getTime() <= this.timeSelection[1].getTime()

            isSelected =
              isSelected &&
              ((isNDSC && row.brkDims[lollipopId] !== '_T') ||
                row.brkDims[lollipopId] === mainOption?.id)

            return isSelected && isCountrySelected(row[3])
          })

        const broadbandFilter = /broadband/i.test(this.mainCubeId) && this.timeScale.id === 'Q'
        data.fullfillHeatmapGrid(broadbandFilter)
      }

      if (this.lastDates) {
        data = data.lastAvailableData
        if (this.countryFilter) {
          const filteredData = [data[0]]
          filteredData.countries = data.countries
          filteredData.dates = data.dates
          filteredData.range = [Infinity, -Infinity]
          filteredData.dataset = data.dataset
          filteredData.lollipopInfo = data.lollipopInfo
          filteredData.lollipopData = data.lollipopInfo
            ? Object.fromEntries(Object.keys(data.lollipopData).map(opt => [opt, []]))
            : []
          for (let row of data) {
            if (isCountrySelected(row[data.fields._COUNTRY_])) {
              filteredData.countries[row[data.fields._COUNTRY_]] = true
              filteredData.range = data.updateRange(filteredData.range, row[data.fields._VALUE_])
              filteredData.push(row)
              for (let opt in data.lollipopData) {
                let lollipopRow = data.lollipopData[opt].find(
                  d => d[data.fields._COUNTRY_] === row[data.fields._COUNTRY_]
                )
                if (!lollipopRow) {
                  lollipopRow = row.map((d, i) => (i !== data.fields._VALUE_ ? d : null))
                }
                filteredData.lollipopData[opt].push(lollipopRow)
              }
            }
          }
          data = data.inherit(filteredData)
        }
      }

      if (this.chartType === 'barchart') data = data.sort((d1, d2) => d2[1] - d1[1])
      if (data.lollipopData) data = data.mergeLollipopData()

      return data
    },
    filteredDataCountries() {
      return this.mapIsoCodes && this.chartType === 'choropleth'
        ? this.dataCountries.filter(cou => this.mapIsoCodes[cou.id])
        : this.dataCountries
    },
  },
  watch: {
    currentChartData: {
      handler(to) {
        let opts = []
        if (to && to.countries) {
          const filter = Object.keys(to.countries).reduce((acum, iso) => {
            acum[iso] = to.countries[iso] !== false
            return acum
          }, {})
          const countryDimension = this.$store.getters.mainDimensions.find(
            d => d.type === 'country'
          )
          opts = pruneTree(countryDimension.options, filter)
        }
        this.dataCountries = opts
      },
      immediate: true,
      depth: true,
    },
    selectedCountries(to) {
      if (!to || Object.keys(to).length === 0) {
        if (this.$store.state.query.countryFilter) this.$store.commit('countryFilter', false)
      }
    },
    disabledChartTypes(to) {
      if (to.map(e => e[0]).includes(this.chartType)) {
        this.chartType = 'barchart'
      }
    },
  },
  methods: {
    storeIsoCodes(isoCodes) {
      this.mapIsoCodes = isoCodes
    },
  },
}
</script>

<style lang="stylus" scoped>
.indicator
  display: flex
  flex: 1 1 0%
  flex-direction: column
  padding: 0 vw(75px) vw(20px)

  .mobile &
    padding: 0 vw(20px) vw(60px)

    +m-breakpoint(landscape)
      padding: 0 vw(20px) vh(90px)

  &[layout="embedded"],
  &[layout="print"],
  &[layout="thumbnail"]
    padding: 0 vw(50px)

    .indicator__outputs .chart
      padding-right: 0

  &[layout="embedded"]
    .mobile &
      padding: 0 vw(10px)

      +m-breakpoint(landscape)
        padding: 0 vw(10px) vh(20px)

.indicator__outputs
  display: flex
  flex: 1 1 0%
  flex-direction: column
  position: relative

  .mobile &
    min-height: 45vh

    +m-breakpoint(landscape)
      min-height: 90vh

  .indicator__chart-header
    display: flex
    justify-content: flex-end

    .mobile &
      justify-content: flex-start

    .time-scale-selector
      display: flex
      align-items: center

    .unit-selector
      display: flex
      align-items: center

  .indicator__error,
  .indicator__loader
    height: 100%
    pointer-events: none
    width: 100%

    .text
      font-size: 1.5rem
      left: 50%
      position: absolute
      top: 50%
      transform: translate(-50%, -50%)

      .mobile &
        +m-breakpoint(landscape)
          top: calc(50% - 15px)

  .indicator__loader
    background-image: url("../../assets/images/oecd-loader.gif")
    background-position: center
    background-repeat: no-repeat
    background-size: 100px

    .mobile &
      background-size: 100px

    .text
      padding-top: 180px

      .mobile &
        padding-top: 140px

  .data-table
    flex: 1 1 auto
    margin: 20px

  .chart-type-selector
    padding-right: vw(75px)

  .chart-container
    display: flex
    flex: 1 1 0%

    .mobile &
      padding-top: vw(20px)
      flex-direction: column

      +m-breakpoint(landscape)
        padding-top: vh(20px)

  .chart
    flex: 1 1 50%
    padding-right: vw(50px)

    .mobile &
      padding-right: unset

.indicator__controls-pannel
  position: absolute
  top: 5px
  right: 0px

  +m-breakpoint(sm md)
    top: 0px

  .mobile &
    align-self: flex-end
    order: -1

.indicator__time-selector
  align-items: center
  display: flex
  margin: vw(10px) 0

  .mobile &
    margin: 0

  .indicator__time-mode
    margin-left: vw(30px)

    .mobile &
      margin-left: vw(15px)
      display: flex
      align-items: center

    label
      font-size: 0.7em

      .mobile &
        padding-left: vw(10px)
        font-size: 0.6em
        width: 65px

        +m-breakpoint(landscape)
          width: 85px

      &.fade-out
        opacity: 0.5
</style>
